import { NgClass } from '@angular/common';

export enum Breakpoint {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl'
}

export type BreakpointKey = Breakpoint | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export enum BreakpointRangeKey {
  MIN = 'min',
  MAX = 'max'
}

export interface BreakpointRange {
  min: number;
  max?: number;
}

export class CssUtils {
  static baseSize = 16;
  static baseLine = 11;

  // global HDMS CSS breakpoint definitions
  // TODO: if possible try to somehow retrieve from CSS
  private static readonly breakpoints = {
    xs: { min: 0, max: 767 },
    sm: { min: 768, max: 1023 },
    md: { min: 1024, max: 1439 },
    lg: { min: 1440, max: 2559 },
    xl: { min: 2560 }
  };

  /**
   * returns a particular BreakpointRange object
   * @param bp
   */
  static getBreakpoint(bp: BreakpointKey): BreakpointRange {
    return CssUtils.breakpoints[bp] || { min: null, max: null };
  }

  /**
   * returns related media query for a given breakpoint
   * optionally limit to either min or max width
   * @param bp
   * @param limit
   */
  static getMediaQuery(bp: BreakpointKey, limit?: BreakpointRangeKey): string {
    const breakpointLimits = CssUtils.getBreakpoint(bp);
    let mediaQuery = '';

    if (breakpointLimits.min && limit !== BreakpointRangeKey.MAX) {
      mediaQuery += `(min-width: ${breakpointLimits.min}px)`;
    }

    if (breakpointLimits.max && limit !== BreakpointRangeKey.MIN) {
      if (mediaQuery) {
        mediaQuery += ' and ';
      }
      mediaQuery += `(max-width: ${breakpointLimits.max}px)`;
    }

    return mediaQuery;
  }

  /**
   * converts input to rem value:
   * - string containing number + px
   * - integer assumed to be px-based
   * already rem-based values or any non-matching strings will be passed through
   * @param value
   */
  static pxToRem(value: number | string): string {
    value = value.toString();
    const pxValRegex = /^(\d+)(px)?$/;
    if (value.match(pxValRegex)) {
      value = `${Math.round((parseInt(value.replace(pxValRegex, '$1'), 10) / this.baseSize) * 1000) / 1000}rem`;
    }

    return value;
  }

  /**
   * calculates rem value based on number of lines applied
   * @param num
   */
  static linesToRem(num: number): number {
    return (num * CssUtils.baseLine) / CssUtils.baseSize;
  }

  /**
   * Concatenates the values of arbitrary parameters to a proper classList string
   * Parameter type (NgClass['ngClass']) could be string | string[] | Set<string> | { [klass: string]: any }
   * Falsy values will be filtered out
   * @param rest
   */
  static getJoinedClassList(...rest: NgClass['ngClass'][]): string {
    return rest
      .reduce<string[]>((acc: string[], curr: NgClass['ngClass']) => {
        if (curr) {
          if (typeof curr === 'string') {
            return [...acc, curr];
          }
          if (curr instanceof Set) {
            curr = Array.from(curr);
          }
          if (Array.isArray(curr)) {
            return [...acc, ...curr.filter(Boolean)];
          }
          if (typeof curr === 'object') {
            return [...acc, ...Object.keys(curr).filter(k => curr[k])];
          }
        }
        return acc;
      }, [])
      .join(' ');
  }
}
